import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import vnpfLogo from '../assets/vnpf-logo.png';

// TS port of the source (Falah) app's pages/FacilityForm.jsx. Photo
// placeholder uses the app's own logo asset (matching the pattern in
// pages/system/UserForm.tsx) instead of the source's
// `publicAsset('favicon-48x48.png')` — that specific public asset isn't
// part of this app's build.
interface FacilityFormState {
  name: string;
  building: string;
  floor: string;
  capacity: string;
  description: string;
  maintenance_notes: string;
  status: string;
}

const emptyForm: FacilityFormState = {
  name: '',
  building: '',
  floor: '',
  capacity: '',
  description: '',
  maintenance_notes: '',
  status: 'active',
};

interface FacilityRecord {
  id: number;
  name: string;
  building: string | null;
  floor: string | null;
  capacity: number | null;
  description: string | null;
  maintenance_notes: string | null;
  status: string;
  photo_url: string | null;
}

type FormErrors = Record<string, string[]>;

export function FacilityForm() {
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState<FacilityFormState>(emptyForm);
  const [facility, setFacility] = useState<FacilityRecord | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isNew) return;
    api.get<FacilityRecord>(`/facilities/${id}`).then((res) => {
      setForm({
        name: res.data.name,
        building: res.data.building ?? '',
        floor: res.data.floor ?? '',
        capacity: res.data.capacity != null ? String(res.data.capacity) : '',
        description: res.data.description ?? '',
        maintenance_notes: res.data.maintenance_notes ?? '',
        status: res.data.status,
      });
      setFacility(res.data);
      setLoaded(true);
    });
  }, [id, isNew]);

  const set =
    (key: keyof FacilityFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload: Record<string, unknown> = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      if (isNew) {
        const res = await api.post<FacilityRecord>('/facilities', payload);
        navigate(`/resources/facilities/${res.data.id}/edit`);
      } else {
        const res = await api.put<FacilityRecord>(`/facilities/${id}`, payload);
        setFacility(res.data);
      }
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  const pickPhoto = () => fileInputRef.current?.click();

  const onPhotoSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPhotoError('');
    const data = new FormData();
    data.append('photo', file);
    setUploading(true);
    try {
      const res = await api.post<FacilityRecord>(`/facilities/${id}/photo`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFacility(res.data);
    } catch (err) {
      setPhotoError(err instanceof ApiError ? err.fieldError('photo') ?? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (!(await confirmDelete('Remove this photo?', { confirmLabel: 'Remove' }))) return;
    setUploading(true);
    try {
      const res = await api.delete<FacilityRecord>(`/facilities/${id}/photo`);
      setFacility(res.data);
    } finally {
      setUploading(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <h1>{isNew ? 'Add facility' : 'Edit facility'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}

          {!isNew && (
            <div className="span-2 icon-upload">
              <img src={facility?.photo_url ?? vnpfLogo} alt="" className="icon-upload-preview" />
              <div className="icon-upload-actions">
                <button type="button" className="btn btn-ghost" onClick={pickPhoto} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload photo'}
                </button>
                {facility?.photo_url && (
                  <button type="button" className="btn btn-danger-ghost" onClick={removePhoto} disabled={uploading}>
                    Remove photo
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onPhotoSelected}
                hidden
              />
              {photoError && <div className="alert alert-error">{photoError}</div>}
            </div>
          )}
          {isNew && <p className="span-2 muted">Save the facility first, then you can add a photo.</p>}

          <label className="span-2">
            Name *
            <input value={form.name} onChange={set('name')} required />
            {errors.name && <small className="error">{errors.name[0]}</small>}
          </label>
          <label>
            Building
            <input value={form.building} onChange={set('building')} />
          </label>
          <label>
            Floor
            <input value={form.floor} onChange={set('floor')} />
          </label>
          <label>
            Capacity
            <input type="number" min="0" value={form.capacity} onChange={set('capacity')} />
          </label>
          <label>
            Status
            <select value={form.status} onChange={set('status')}>
              {listOptions(lists, 'facility_status').map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="span-2">
            Description
            <textarea rows={2} value={form.description} onChange={set('description')} />
          </label>
          <label className="span-2">
            Maintenance notes
            <textarea rows={2} value={form.maintenance_notes} onChange={set('maintenance_notes')} />
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/resources/facilities">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
